import React from 'react';
import classnames from 'classnames';
import Markdown, { MarkdownToJSX } from 'markdown-to-jsx';
import deepMerge from 'deepmerge';
import { Heading, HeadingProps, Text, TextLink, TextLinkProps, TextProps } from '../index';
import styles from './richtext.module.scss';

export type RichTextType = {
  mdString: string;
  isLegal?: boolean;
  wrapperElement?: 'article' | 'div' | 'section';
  customOverrides?: MarkdownToJSX.Overrides;
  className?: string;
  enableParsingRawHtml?: boolean;
};

export type RichtextTextLinkType = Omit<TextLinkProps, 'linkText'> & {
  children: string;
};

const baseClass = 'richtext';

const RichtextTextLink: React.FC<RichtextTextLinkType> = ({ children, ...props }) => (
  <TextLink {...props} linkText={children} />
);

export const defaultOverrides: MarkdownToJSX.Overrides = {
  h1: {
    component: Heading,
    props: {
      tag: 'h1',
      variant: 'display-functional-200',
      className: styles.h1
    } as HeadingProps
  },
  h2: {
    component: Heading,
    props: {
      tag: 'h2',
      variant: 'headline-functional-100',
      className: styles.h2
    } as HeadingProps
  },
  h3: {
    component: Heading,
    props: {
      tag: 'h3',
      variant: 'headline-functional-200',
      className: styles.h3
    } as HeadingProps
  },
  h4: {
    component: Heading,
    props: {
      tag: 'h4',
      variant: 'headline-functional-300',
      className: styles.h4
    } as HeadingProps
  },
  h5: {
    component: Heading,
    props: {
      tag: 'h5',
      variant: 'headline-functional-400',
      className: styles.h5
    } as HeadingProps
  },
  h6: {
    component: Heading,
    props: {
      tag: 'h6',
      variant: 'headline-functional-400',
      className: styles.h6
    } as HeadingProps
  },
  table: {
    props: {
      className: styles.table
    }
  },
  blockquote: {
    props: {
      className: styles.defaultText
    }
  },
  code: {
    props: {
      className: styles.defaultText
    }
  },
  pre: {
    props: {
      className: styles.defaultText
    }
  },
  p: {
    props: {
      tag: 'p',
      variant: 'paragraph-100',
      className: styles.p
    } as TextProps,
    component: Text
  },
  a: {
    component: RichtextTextLink,
    props: {
      variant: 'hyperlink-100',
      className: styles.a
    } as TextLinkProps
  },
  ul: {
    props: {
      className: styles.ul
    }
  },
  ol: {
    props: {
      className: styles.ol
    }
  },
  li: {
    props: {
      className: styles.li
    }
  },
  frame: {
    props: {
      tag: 'div',
      className: styles.frame
    }
  }
};

const legalOverrides: MarkdownToJSX.Overrides = {
  p: {
    props: {
      tag: 'p',
      variant: 'legal-100',
      className: styles.p
    } as TextProps,
    component: Text
  },
  a: {
    component: RichtextTextLink,
    props: {
      variant: 'hyperlink-legal',
      className: styles.a
    } as TextLinkProps
  }
};

export const RichText: React.FC<RichTextType> = ({
  className,
  mdString,
  isLegal,
  wrapperElement,
  customOverrides = {},
  enableParsingRawHtml
}) => {
  const classes = classnames(styles[baseClass], className, {
    [styles[`${baseClass}--legal`]]: isLegal
  });

  const children = (mdString ?? '')
    .replace(/\\(?=[.?*+[\]{}()^$|\\])/gi, '')
    .replace(/\\n/gi, '\n')
    .replace(
      /\\&nbsp;/gi,
      `\
      `
    );

  const overrides: MarkdownToJSX.Overrides = React.useMemo(
    () =>
      deepMerge<MarkdownToJSX.Overrides, MarkdownToJSX.Overrides>(
        { ...defaultOverrides, ...(isLegal ? legalOverrides : {}) },
        customOverrides
      ),
    [isLegal, customOverrides]
  );

  return (
    <Markdown
      options={{
        overrides,
        forceBlock: true,
        disableParsingRawHTML: !enableParsingRawHtml,
        wrapper: wrapperElement,
        forceWrapper: true
      }}
      className={classes}
      data-testid="richtext-component"
    >
      {children}
    </Markdown>
  );
};
